import { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'

/**
 * Hook for fetching media files (images, videos) from the download API as blob URLs.
 * Handles loading, error states, and cleanup of object URLs on unmount.
 */
export const useMediaUrl = (
  customer: string,
  job: string,
  filePath: string,
): {
  url: string | null
  loading: boolean
  error: string | null
} => {
  const [url, setUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const urlRef = useRef<string | null>(null)

  useEffect(() => {
    // Cleanup previous URL
    if (urlRef.current) {
      URL.revokeObjectURL(urlRef.current)
      urlRef.current = null
    }

    if (!customer || !job || !filePath) {
      setUrl(null)
      setLoading(false)
      setError(null)
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    axios
      .get(`${API_BASE_URL}/files/blob`, {
        params: {
          customer,
          job,
          path: filePath,
        },
        responseType: 'blob',
      })
      .then((res) => {
        if (cancelled) return
        const objectUrl = URL.createObjectURL(res.data)
        urlRef.current = objectUrl
        setUrl(objectUrl)
        setLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        const message = err?.response?.data?.error || err.message || 'Failed to load media'
        setError(typeof message === 'string' ? message : 'Failed to load media')
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [customer, job, filePath])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current)
      }
    }
  }, [])

  return { url, loading, error }
}

export default useMediaUrl
